import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Flame, CalendarCheck, ArrowDown, MapPin, Clock, Wine } from 'lucide-react';

interface HeroSectionProps {
  onOpenOrderApp: () => void;
  onExploreFood: () => void;
}

const VIDEO_MODES = ['restaurant', 'hearth', 'bar'] as const;
type VideoMode = typeof VIDEO_MODES[number];

export const HeroSection: React.FC<HeroSectionProps> = ({ onOpenOrderApp, onExploreFood }) => {
  const { lang } = useLanguage();
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const activeVideo = VIDEO_MODES[activeVideoIndex];

  // Auto-rotate slider and video every 5 seconds
  React.useEffect(() => {
    const timer = setInterval(() => {
      setActiveVideoIndex((prev) => (prev + 1) % VIDEO_MODES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [activeVideoIndex]);

  const handleSelectMode = (index: number) => {
    setActiveVideoIndex(index);
  };

  const text = {
    badge: {
      fi: 'HELSINKI · PUU-UUNI & ARTESAANIBAARI',
      en: 'HELSINKI · WOOD-FIRED HEARTH & CRAFT BAR',
      sv: 'HELSINGFORS · VEDUGN & HANTVERKSBAR'
    },
    title: {
      fi: (
        <>
          Tulen hehku, <em>pohjoisen maut</em> ja Helsingin parhaat illat.
        </>
      ),
      en: (
        <>
          Warmth of the flame, <em>Nordic flavors</em>, and evenings well spent.
        </>
      ),
      sv: (
        <>
          Eldens värme, <em>nordiska smaker</em> och kvällar värda att minnas.
        </>
      )
    },
    desc: {
      fi: 'Tervetuloa Aleksanterinkadulle. Valmistamme artesaanipizzamme 48 tunnin hapanjuureen ja paistamme ne 450-asteisessa tulikiviuunissa. Tiskin takana ravistamme raikkaat marjacocktailit ja kaadamme kotimaiset pienpanimo-oluet.',
      en: 'Welcome to Aleksanterinkatu 18. We handcraft our pizzas on 48-hour fermented sourdough blistered in a 450°C stone oven, shake fresh botanical cocktails at the bar, and offer an intimate space made for good food and great company.',
      sv: 'Välkommen till Alexandersgatan 18. Vi bakar surdegspizzor i 450-graders stenugn, blandar fräscha hantverkscocktails och bjuder in till en varm gemenskap mitt i Helsingfors.'
    },
    btnBook: {
      fi: 'Varaa Pöytä / Tilaa Pöytään',
      en: 'Book Your Table',
      sv: 'Boka Ditt Bord'
    },
    btnMenu: {
      fi: 'Selaa Ruokalistaa ↓',
      en: 'Explore Menu ↓',
      sv: 'Se Menyn ↓'
    },
    address: 'Aleksanterinkatu 18, 00170 Helsinki',
    hoursShort: {
      fi: 'Avoinna ma–su lounaasta myöhäisiltaan',
      en: 'Open daily from lunch to late night',
      sv: 'Öppet dagligen från lunch till sen kväll'
    }
  };

  return (
    <section id="hero" className="os-hero-section">
      {/* Background Media: High Definition Restaurant Video Auto-Rotating every 5s */}
      <div className="os-hero-media-wrapper">
        <video
          key={activeVideo}
          autoPlay
          muted
          loop
          playsInline
          poster="/images/hero-restaurant-poster.png"
          className="os-hero-backdrop"
        >
          {activeVideo === 'restaurant' && (
            <source src="/videos/restaurant-ambiance.mp4" type="video/mp4" />
          )}
          {activeVideo === 'hearth' && (
            <source src="/videos/hero-food-bar.webm" type="video/webm" />
          )}
          {activeVideo === 'bar' && (
            <source src="/videos/hero-bar.webm" type="video/webm" />
          )}
          <img
            src="/images/hero-bar-hearth.jpg"
            alt="The Oven Story Helsinki warm wood-fired oven and bar"
            className="os-hero-backdrop"
          />
        </video>
        <div className="os-hero-gradient" />

        {/* Ambient Video Atmosphere Switcher with 5s Auto Rotation (Bottom Right) */}
        <div className="os-hero-video-switcher">
          <button
            onClick={() => handleSelectMode(0)}
            className={`os-video-switch-btn ${activeVideo === 'restaurant' ? 'active' : ''}`}
            title="Ravintolatunnelma ja pöydät (Restaurant Dining Room)"
          >
            <Wine size={12} />
            <span>{lang === 'fi' ? 'Ravintola' : 'Dining Room'}</span>
            {activeVideo === 'restaurant' && <span className="os-switch-progress" />}
          </button>
          <button
            onClick={() => handleSelectMode(1)}
            className={`os-video-switch-btn ${activeVideo === 'hearth' ? 'active' : ''}`}
            title="Puu-uuni ja liekit (Wood-Fired Hearth)"
          >
            <Flame size={12} />
            <span>{lang === 'fi' ? 'Puu-uuni' : 'Wood Oven'}</span>
            {activeVideo === 'hearth' && <span className="os-switch-progress" />}
          </button>
          <button
            onClick={() => handleSelectMode(2)}
            className={`os-video-switch-btn ${activeVideo === 'bar' ? 'active' : ''}`}
            title="Cocktailbaari (Craft Cocktail Bar)"
          >
            <Wine size={12} />
            <span>{lang === 'fi' ? 'Baari' : 'Bar'}</span>
            {activeVideo === 'bar' && <span className="os-switch-progress" />}
          </button>
        </div>
      </div>

      <div className="os-container">
        <div className="os-hero-content">
          {/* Eyebrow Badge */}
          <div className="os-hero-badge">
            <Flame size={14} color="#f59e0b" style={{ fill: '#f59e0b' }} />
            <span>{text.badge[lang]}</span>
          </div>

          {/* Heading */}
          <h1 className="os-hero-title">
            {text.title[lang]}
          </h1>

          {/* Natural Human Storytelling */}
          <p className="os-hero-description">
            {text.desc[lang]}
          </p>

          {/* Clear Actions */}
          <div className="os-hero-cta-group">
            {/* Primary Action Button (Redirects to Table Ordering / Booking App) */}
            <button onClick={onOpenOrderApp} className="os-btn-hero-primary">
              <CalendarCheck size={18} />
              <span>{text.btnBook[lang]}</span>
            </button>

            {/* Smooth Scroll to Menu */}
            <button onClick={onExploreFood} className="os-btn-hero-secondary">
              <span>{text.btnMenu[lang]}</span>
              <ArrowDown size={15} />
            </button>
          </div>

          {/* Info Strip */}
          <div className="os-hero-info-strip">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <MapPin size={15} color="var(--os-copper)" />
              <span>{text.address}</span>
            </div>
            <span>•</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={15} color="var(--os-amber)" />
              <span>{text.hoursShort[lang]}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
